// src/components/management/useTeamActions.js
//
// The three writes that team management performs: saving a company's team list,
// moving a person into or out of a team, and linking a driver to the dispatcher
// who runs them.
//
// Extracted here because Teams is now reachable from two places — its own tab
// under Management, and nested inside Company Management where it has always
// been. Two copies of this logic would drift, and drift is what has caused most
// of the confusing behaviour in this area: a driver who moved company but kept
// an old dispatcher, a team that listed someone the load board did not.
//
// Everything about WHY each write is shaped the way it is stays with the write
// itself, so whichever screen calls it behaves identically.

import { useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp, collection, query, where, getDocs, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { logAudit } from '../../utils/auditLog';
import { withCompanyHistory } from '../../utils/companyScope';
import { getCompanyTeams, getUserTeamIds, teamDispatchers, driverHandovers } from '../../utils/teams';
import { LEFT_TEAM_ACCESS, deriveLoadTeamId, driverOwnTeam } from '../../utils/loadTeam';
import { DEFAULT_CURRENT_STATUSES } from '../loads/utils/constants';
import { notifyUsers, teamMainAdminIds, teamLabel, nameOf, personMoveNotes } from '../../utils/notify';
import { planLoadHandovers, loadHandoverUpdate, planRejoinLoads, rejoinUpdate } from '../../utils/loadHandover';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function useTeamActions({ loggedInUser, tenantId, users, drivers, companies = [], canManage, onError }) {
  const [teamBusy, setTeamBusy] = useState(false);

  const fail = useCallback((message, error) => {
    console.error(message, error);
    if (onError) onError({ type: 'error', text: message });
  }, [onError]);

  /**
   * Bring a driver's loads along when they are back with their dispatcher on a
   * new team (utils/loadHandover, planRejoinLoads), and tell the people
   * involved. `driver` is the driver as they are now. Never throws: the move
   * or link that called it has already been saved.
   */
  const rejoinDriverLoads = useCallback(async (driver, dispatcherId) => {
    try {
      const plan = await planRejoinLoads({ tenantId, driver, dispatcherId, users, drivers });
      if (plan.length === 0) return;
      for (let i = 0; i < plan.length; i += 400) {
        const batch = writeBatch(db);
        plan.slice(i, i + 400).forEach(r => batch.update(r.ref,
          rejoinUpdate(r, { driver, dispatcherId, actorId: loggedInUser?.uid })));
        await batch.commit();
      }
      await logAudit({
        userId: loggedInUser?.uid,
        userEmail: loggedInUser?.email,
        action: 'LOADS_FOLLOWED_DRIVER',
        targetType: 'driver',
        targetId: driver.id,
        details: { name: driver.name || driver.id, teamId: driver.teamId, dispatcherId, loads: plan.map(r => r.ref.id) },
        tenantId,
      });
      const driverName = driver.name || 'A driver';
      const toTeam = teamLabel(companies, driver.teamId);
      const notes = [];
      plan.forEach(r => {
        notes.push({ userId: dispatcherId,
          message: `Load ${r.loadId} moved with driver ${driverName} to your team ${toTeam}. You can keep working on it.` });
        teamMainAdminIds(users, r.fromTeamId).forEach(uid => notes.push({ userId: uid,
          message: `Load ${r.loadId} moved with driver ${driverName} and dispatcher ${nameOf(users, dispatcherId)} to team ${toTeam}. You can still see it until it is delivered.` }));
        teamMainAdminIds(users, driver.teamId).forEach(uid => notes.push({ userId: uid,
          message: `Load ${r.loadId} came with driver ${driverName} to your team ${toTeam}.` }));
      });
      await notifyUsers({ tenantId: driver.tenantId || tenantId, actorId: loggedInUser?.uid, notes });
    } catch (error) {
      console.error('Teams: could not bring loads along with their driver', error);
    }
  }, [tenantId, users, drivers, companies, loggedInUser]);

  /** Replace a company's team list. */
  const saveTeams = useCallback(async (parent, nextTeams) => {
    if (!canManage || teamBusy) return;
    setTeamBusy(true);
    try {
      await updateDoc(doc(db, 'companies', parent.id), {
        teams: nextTeams,
        updatedAt: serverTimestamp(),
      });

      await logAudit({
        userId: loggedInUser?.uid,
        userEmail: loggedInUser?.email,
        action: 'COMPANY_TEAMS_UPDATED',
        targetType: 'company',
        targetId: parent.id,
        details: { companyName: parent.name, teamCount: nextTeams.length },
        tenantId,
      });
    } catch (error) {
      fail('Could not save teams. Nothing was changed.', error);
    } finally {
      setTeamBusy(false);
    }
  }, [canManage, teamBusy, loggedInUser, tenantId, fail]);

  /**
   * Put someone on a team, or take them off it (teamId null).
   *
   * A team belongs to a company, so being on a team means being in that
   * company. Joining a team of a company somebody is not in MOVES them: they
   * leave every team and every company they were in, and join this one — one write, so the Teams screen, the load pickers and the
   * Companies tab can never disagree about where they are.
   *
   * Taking somebody off a team (teamId null) keeps them in the company, in its
   * "Not in a team here" pool. Either way, from then on they see only that
   * team's loads booked before they left (utils/loadAccess), and their drivers
   * there are handed over.
   */
  const assignPersonToTeam = useCallback(async (parent, { kind, id, teamId, loadAccess, moveLoads, profileCompany }) => {
    if (!canManage || teamBusy) return;
    setTeamBusy(true);
    try {
      if (kind === 'driver') {
        const update = { teamId: teamId || null, updatedAt: serverTimestamp() };
        const driverBeingMoved = drivers.find(d => d.id === id);

        // Remember the team they leave, and when, as for users. Old loads with
        // no teamId and no dispatcher on a team are placed by their driver's
        // team (utils/loadTeam); without this record they would follow the
        // driver, and the old team would lose its delivered history.
        if (driverBeingMoved?.teamId && driverBeingMoved.teamId !== (teamId || null)) {
          update.formerTeams = [
            ...(Array.isArray(driverBeingMoved.formerTeams) ? driverBeingMoved.formerTeams : []),
            { teamId: driverBeingMoved.teamId, removedAt: new Date().toISOString(), removedBy: loggedInUser?.uid || null },
          ];
        }

        // A driver brought in from another company has to be moved, not just
        // given a team: a team lists drivers whose parent company is this one,
        // so writing teamId alone would make them vanish from both sides. The
        // previous company is recorded first so it keeps sight of past work.
        if (teamId && driverBeingMoved && driverBeingMoved.parentCompanyId !== parent.id) {
          update.parentCompanyId = parent.id;
          update.parentCompanyName = parent.name || null;
          update.companyHistoryIds = withCompanyHistory(driverBeingMoved, parent.id);
          // Ticked in the move window: the profile company, which the BOL
          // paperwork prints, becomes the new company too. Unticked, it stays.
          if (profileCompany) {
            update.assignedCompanyId = parent.id;
            update.assignedCompanyName = parent.name || null;
          }
        }

        if (teamId) {
          // A driver on a team but linked to no dispatcher is invisible to
          // every dispatcher — the team box would show them while nobody could
          // work them. Where the team has exactly one dispatcher there is
          // nothing to decide, so make the link now rather than relying on
          // somebody remembering a second step.
          const onTeam = teamDispatchers(users, teamId);

          // A dispatcher already on the new team stays. Otherwise the old link
          // is from the team being left and must not survive the move — it is
          // how drivers ended up "dispatched" by someone on another team.
          const keepsDispatcher = Boolean(driverBeingMoved?.dispatcherId) &&
            onTeam.some(u => u.id === driverBeingMoved.dispatcherId);
          if (!keepsDispatcher) {
            // More than one: left empty on purpose. TeamsPanel shows the driver
            // in a "choose a dispatcher" state that cannot be overlooked.
            update.dispatcherId = onTeam.length === 1 ? onTeam[0].id : null;
          }
        } else {
          // Out of every team means out of every dispatcher's list too.
          update.dispatcherId = null;
        }

        // The loads are read before anything is written, so a failed read
        // leaves the driver where they were rather than moved without them.
        const fromTeamId = driverOwnTeam(driverBeingMoved);
        const snap = moveLoads && teamId && fromTeamId && fromTeamId !== teamId
          ? await getDocs(query(
            collection(db, 'loads'),
            where('tenantId', '==', tenantId),
            where('driverId', '==', id),
            where('status', 'in', DEFAULT_CURRENT_STATUSES),
          ))
          : null;

        await updateDoc(doc(db, 'drivers', id), update);

        // Moved to another team of the same company, with the Super Admin's
        // confirmation: the driver's unfinished loads on the old team go too,
        // to the new team and the driver's new dispatcher. Each load records
        // where it came from (teamMoves), which is what lets the old team still
        // see it — read-only, marked as managed by the new team — until it is
        // finished (utils/loadAccess). Finished loads stay with the old team.
        let movedCount = 0;
        if (snap) {
          const usersById = new Map(users.map(u => [u.id, u]));
          const driversById = new Map(drivers.map(d => [d.id, d]));
          const toDispatcherId = 'dispatcherId' in update ? update.dispatcherId : (driverBeingMoved?.dispatcherId || null);
          const movedAt = new Date().toISOString();
          const moving = snap.docs.filter(d => {
            const load = { id: d.id, ...d.data() };
            return !load.isArchived && deriveLoadTeamId(load, usersById, driversById) === fromTeamId;
          });
          // A batch holds at most 500 writes.
          for (let i = 0; i < moving.length; i += 400) {
            const batch = writeBatch(db);
            for (const d of moving.slice(i, i + 400)) {
              batch.update(d.ref, {
                teamId,
                dispatcherId: toDispatcherId,
                teamMoves: arrayUnion({
                  fromTeamId,
                  toTeamId: teamId,
                  fromDispatcherId: d.data().dispatcherId || null,
                  movedAt,
                  movedBy: loggedInUser?.uid || null,
                }),
                updatedAt: serverTimestamp(),
              });
            }
            await batch.commit();
          }
          movedCount = moving.length;

          await logAudit({
            userId: loggedInUser?.uid,
            userEmail: loggedInUser?.email,
            action: 'DRIVER_MOVED_TO_TEAM',
            targetType: 'driver',
            targetId: id,
            details: {
              name: driverBeingMoved?.name || id,
              teamId: { oldValue: fromTeamId, newValue: teamId },
              loadsMoved: moving.map(d => d.id),
            },
            tenantId,
          });
        }

        // Tell the dispatchers and Main Admins on both sides (the bell, no email).
        const toTeamId = teamId || null;
        if (driverBeingMoved && fromTeamId !== toTeamId) {
          const driverName = driverBeingMoved.name || 'A driver';
          const fromDisp = driverBeingMoved.dispatcherId || null;
          const toDisp = 'dispatcherId' in update ? update.dispatcherId : fromDisp;
          const where = toTeamId ? `team ${teamLabel(companies, toTeamId)}` : 'no team';
          const loadsWent = movedCount > 0 ? ` ${plural(movedCount, 'current load', 'current loads')} went with them.` : '';
          const notes = [];
          if (fromDisp && fromDisp !== toDisp) {
            notes.push({ userId: fromDisp, message: `Driver ${driverName} moved to ${where}. You are no longer their dispatcher.` +
              (movedCount > 0 ? `${loadsWent} You can still see them until delivered.` : ' Their current loads stay with you.') });
          }
          if (toDisp && toDisp !== fromDisp) {
            notes.push({ userId: toDisp, message: `You are now the dispatcher for driver ${driverName}.` +
              (movedCount > 0 ? ` ${plural(movedCount, 'current load', 'current loads')} came with them.` : '') });
          }
          teamMainAdminIds(users, fromTeamId).forEach(uid => notes.push({ userId: uid,
            message: `Driver ${driverName} left your team ${teamLabel(companies, fromTeamId)} for ${where}.` +
              (movedCount > 0 ? loadsWent : ' Any current loads stay with your team.') }));
          teamMainAdminIds(users, toTeamId).forEach(uid => notes.push({ userId: uid,
            message: `Driver ${driverName} joined your team ${teamLabel(companies, toTeamId)}` +
              (toDisp ? ` with dispatcher ${nameOf(users, toDisp)}.` : '. Choose a dispatcher for them.') +
              (movedCount > 0 ? ` ${plural(movedCount, 'current load', 'current loads')} came with them.` : '') }));
          await notifyUsers({ tenantId: driverBeingMoved.tenantId || tenantId, actorId: loggedInUser?.uid, notes,
            extra: { driverName: driverBeingMoved.name || null } });
        }

        // Back with their dispatcher on the new team: loads left behind come too.
        const nowDispatcher = 'dispatcherId' in update ? update.dispatcherId : (driverBeingMoved?.dispatcherId || null);
        if (driverBeingMoved && teamId && nowDispatcher) {
          await rejoinDriverLoads({ ...driverBeingMoved, ...update, teamId }, nowDispatcher);
        }
      } else {
        const user = users.find(u => u.id === id);
        const currentTeamIds = getUserTeamIds(user);
        const companyIds = Array.isArray(user?.assignedParentCompanyIds) ? user.assignedParentCompanyIds : [];
        const joiningFromOutside = Boolean(teamId) && !companyIds.includes(parent.id);

        let nextTeamIds;
        let companyUpdate = null;
        if (joiningFromOutside) {
          // A move: off every team they were on and out of every company they
          // were in — including ones where they were on no team — into this
          // company and this team. Someone dispatching for a team works for
          // that team's company, not for the one they came from.
          nextTeamIds = [teamId];
          companyUpdate = {
            assignedParentCompanyIds: [parent.id],
            assignedParentCompanyNames: [parent.name || null],
          };
        } else {
          const companyTeamIds = getCompanyTeams(parent).map(t => t.id);
          const kept = currentTeamIds.filter(t => !companyTeamIds.includes(t));
          nextTeamIds = teamId ? [...kept, teamId] : kept;
        }

        // Remember which teams they just left, and when. Without it someone
        // taken off a team fell back to seeing the whole company — including
        // every new load booked after they left. loadAccess is the Super
        // Admin's answer for their own unfinished loads there ('manage',
        // 'view' or 'none'; utils/loadAccess); when nobody was asked, 'view'.
        // An ISO string rather than a server timestamp: Firestore does not
        // allow server timestamps inside arrays.
        const left = currentTeamIds.filter(t => !nextTeamIds.includes(t));
        const access = LEFT_TEAM_ACCESS.includes(loadAccess) ? loadAccess : 'view';

        // Read before anything is written: their drivers and unfinished loads
        // on the teams they leave, and who takes each over.
        const handovers = driverHandovers({ leavingUserId: id, leftTeamIds: left, drivers, users });
        const loadHandovers = await planLoadHandovers({
          tenantId, leavingUserId: id, leftTeamIds: left, loadAccess: access,
          driverHandovers: handovers, users, drivers,
        });

        const update = { teamIds: nextTeamIds, ...(companyUpdate || {}) };
        if (left.length > 0) {
          const removedAt = new Date().toISOString();
          update.formerTeams = [
            ...(Array.isArray(user?.formerTeams) ? user.formerTeams : []),
            ...left.map(t => ({
              teamId: t,
              removedAt,
              removedBy: loggedInUser?.uid || null,
              loadAccess: access,
              // A Main Admin ran the whole team, so what they keep is the
              // team's unfinished work, not only loads they dispatched.
              asMainAdmin: (Array.isArray(user?.role) ? user.role : [user?.role]).includes('Main Admin'),
            })),
          ];
        }
        await updateDoc(doc(db, 'users', id), update);

        // Their drivers on the teams they left go to that team's other
        // dispatcher, or to nobody (see driverHandovers).
        for (const h of handovers) {
          await updateDoc(doc(db, 'drivers', h.driverId), {
            dispatcherId: h.dispatcherId,
            updatedAt: serverTimestamp(),
          });
        }

        // Their unfinished loads there follow the drivers (utils/loadHandover),
        // unless they keep "See and manage".
        for (let i = 0; i < loadHandovers.length; i += 400) {
          const batch = writeBatch(db);
          loadHandovers.slice(i, i + 400)
            .forEach(h => batch.update(h.ref, loadHandoverUpdate(h, id, loggedInUser?.uid)));
          await batch.commit();
        }

        await notifyUsers({
          tenantId: user?.tenantId || tenantId,
          actorId: loggedInUser?.uid,
          notes: personMoveNotes({ user, id, left, joined: teamId ? [teamId] : [], access, handovers, loadHandovers, users, drivers, companies }),
        });

        if (companyUpdate) {
          // Only what changed (CLAUDE.md, "Writing to the audit log").
          await logAudit({
            userId: loggedInUser?.uid,
            userEmail: loggedInUser?.email,
            action: 'USER_MOVED_TO_TEAM',
            targetType: 'user',
            targetId: id,
            details: {
              name: user?.name || user?.email || id,
              teamIds: { oldValue: currentTeamIds, newValue: nextTeamIds },
              assignedParentCompanyIds: { oldValue: companyIds, newValue: companyUpdate.assignedParentCompanyIds },
              ...(loadHandovers.length > 0 ? { loadsHandedOver: loadHandovers.map(h => h.ref.id) } : {}),
            },
            tenantId,
          });
        }
      }
    } catch (error) {
      fail('Could not change that team assignment.', error);
    } finally {
      setTeamBusy(false);
    }
  }, [canManage, teamBusy, drivers, users, companies, loggedInUser, tenantId, fail, rejoinDriverLoads]);

  /**
   * Link a driver to the dispatcher who runs them, within their team.
   *
   * One driver, at most one dispatcher — so this is a single field on the
   * driver rather than a list on the dispatcher. Passing null unlinks.
   */
  const linkDriverToDispatcher = useCallback(async (driverId, dispatcherId) => {
    if (!canManage || teamBusy) return;
    setTeamBusy(true);
    const driver = drivers.find(d => d.id === driverId);
    const fromId = driver?.dispatcherId || null;
    const toId = dispatcherId || null;
    try {
      await updateDoc(doc(db, 'drivers', driverId), {
        dispatcherId: toId,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      fail('Could not change that dispatcher.', error);
      setTeamBusy(false);
      return;
    }
    // Both dispatchers hear about it in the bell (Header), no email. A failed
    // notification never undoes the change itself.
    // The team's Main Admins too.
    if (fromId !== toId) {
      const name = driver?.name || 'driver';
      const notes = [
        fromId && { userId: fromId, message: `You are no longer the dispatcher for driver ${name}` },
        toId && { userId: toId, message: `You are now the dispatcher for driver ${name}` },
        ...teamMainAdminIds(users, driver?.teamId).map(uid => ({ userId: uid,
          message: `Dispatcher for driver ${name} changed: ${fromId ? nameOf(users, fromId) : 'none'} → ${toId ? nameOf(users, toId) : 'none'}` })),
      ].filter(Boolean);
      await notifyUsers({ tenantId: driver?.tenantId || tenantId, actorId: loggedInUser?.uid, notes,
        extra: { driverName: driver?.name || null } });
    }
    if (driver && toId) await rejoinDriverLoads({ ...driver, dispatcherId: toId }, toId);
    setTeamBusy(false);
  }, [canManage, teamBusy, drivers, users, loggedInUser, tenantId, fail, rejoinDriverLoads]);

  /**
   * Drop team entries that no longer count — a team of a company the user is
   * not in (see getUserTeamIds). They are ignored everywhere already; this only
   * tidies the record, so nothing anyone sees changes.
   */
  const removeLeftoverTeams = useCallback(async (userId) => {
    if (!canManage) return;
    const user = users.find(u => u.id === userId);
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', userId), { teamIds: getUserTeamIds(user) });
    } catch (error) {
      fail('Could not tidy that record.', error);
    }
  }, [canManage, users, fail]);

  return { teamBusy, saveTeams, assignPersonToTeam, linkDriverToDispatcher, removeLeftoverTeams };
}

/**
 * The parent companies this viewer may see teams for.
 *
 * "Parent company" is defined exactly as Company Management defines it — top
 * level, not a subdivision — because a team hangs off a parent company and the
 * two screens must not disagree about which companies exist.
 *
 * Note this reads `assignedParentCompanyIds` (how a USER is scoped to
 * companies), not getCompanyScopeIds (how a DRIVER or TRUCK is). They are
 * different fields and mixing them up shows a Main Admin the wrong list.
 */
export function manageableCompanies(companies, loggedInUser, isSuper) {
  const parents = (companies || [])
    .filter(c => !c.parentCompanyId && c.type !== 'subdivision')
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (isSuper) return parents;

  const assigned = loggedInUser?.assignedParentCompanyIds || [];
  return parents.filter(c => assigned.includes(c.id));
}
